/**
 * useWindowResize — bottom-right-corner resize for a free-floating window.
 *
 * Adapted from the mechanics of `usePanelResize` (ref-shadowed final value so
 * document listeners depend only on `[isResizing]`; body userSelect/cursor
 * lock; commit once on mouseup), but the sizing math is different: a
 * top-left-anchored floating window resizes from **pointer-origin deltas**
 * (the pointer position captured at mousedown + the size at that moment), NOT
 * `window.innerWidth - clientX` (usePanelResize is right-edge-anchored) and NOT
 * `getBoundingClientRect` (returns 0 in happy-dom and forces reflow). This
 * keeps the hook correct for a movable window and assertable in tests by
 * dispatching document mouse events with explicit clientX/clientY.
 *
 * The committed size lives in the store; during a drag the hook returns a local
 * live size for smooth rendering and only calls `onCommit` (→ persisted store
 * write) on mouseup, so localStorage is written once per resize, never per pixel.
 */

import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { MIN_SIZE } from '@/components/FloatingWindow/floatingWindow.constants';
import type { Size } from '@/components/FloatingWindow/floatingWindowGeometry';

export interface UseWindowResizeOptions {
  /** Committed size from the store. */
  size: Size;
  /** Called once on mouseup with the final size (persist via store.setSize). */
  onCommit: (size: Size) => void;
  /** Minimum size; defaults to MIN_SIZE. */
  minSize?: Size;
}

export interface UseWindowResizeResult {
  /** Size to render — live during a drag, committed size otherwise. */
  size: Size;
  isResizing: boolean;
  /** Attach to the corner handle's onMouseDown. */
  onResizeHandleMouseDown: (e: ReactMouseEvent<Element>) => void;
}

export function useWindowResize(
  options: UseWindowResizeOptions
): UseWindowResizeResult {
  const { size, onCommit, minSize = MIN_SIZE } = options;

  const [isResizing, setIsResizing] = useState(false);
  const [liveSize, setLiveSize] = useState<Size | null>(null);

  // Pointer position + size captured at mousedown, and the running final value
  // (so mouseup persists the true final size without listing it as a dep).
  const startPointerRef = useRef({ x: 0, y: 0 });
  const startSizeRef = useRef<Size>(size);
  const sizeRef = useRef<Size>(size);

  const onResizeHandleMouseDown = (e: ReactMouseEvent<Element>) => {
    // Suppress text selection; stop the event from reaching the title-bar drag.
    e.preventDefault();
    e.stopPropagation();
    startPointerRef.current = { x: e.clientX, y: e.clientY };
    startSizeRef.current = size;
    sizeRef.current = size;
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    const viewportCap = () => ({
      w: typeof window !== 'undefined' ? window.innerWidth : Infinity,
      h: typeof window !== 'undefined' ? window.innerHeight : Infinity,
    });

    const handleMouseMove = (e: MouseEvent) => {
      const cap = viewportCap();
      const dx = e.clientX - startPointerRef.current.x;
      const dy = e.clientY - startPointerRef.current.y;
      const next: Size = {
        w: Math.max(minSize.w, Math.min(startSizeRef.current.w + dx, cap.w)),
        h: Math.max(minSize.h, Math.min(startSizeRef.current.h + dy, cap.h)),
      };
      sizeRef.current = next; // synchronous — mouseup reads the true final value
      setLiveSize(next);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setLiveSize(null);
      onCommit(sizeRef.current);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'nwse-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing, minSize.w, minSize.h, onCommit]);

  return {
    size: liveSize ?? size,
    isResizing,
    onResizeHandleMouseDown,
  };
}
